import { DeviceTypes } from '@eppendorf/app-configs';
import {
  GetDeviceSnapshotDTO,
  TelemetryHistoryEnrichedDTO,
  UnitStateV2,
} from '@eppendorf/vnls-device-service-utils';

import { useDeviceTypeConfig } from '$components/app-config/useDeviceTypeConfig';
import { useDeviceParameterAccessor } from '$features/monitoring/hooks/use-device-parameter-accessor';
import {
  findShadowCompByTelemetryId,
  findTelemById,
} from '$features/monitoring/monitoring.utils.snapshot';
import { MonitoringParameterOffline } from '$features/monitoring/parameters/monitoring-parameter-offline';
import { MonitoringParameterSnapshot } from '$features/monitoring/parameters/snapshot/monitoring-parameter-snapshot';
import { isCentrifugeLarge } from '$features/monitoring/shared/centrifuge-large.utils';
import {
  findCountdownTimerShadowComp,
  isCountdownTimerParameter,
} from '$features/monitoring/shared/countdown-timer.utils';
import { hasStaticSetpointDisplay as checkStaticSetpointDisplay } from '$features/monitoring/shared/static-setpoint-display.utils';
import { isThermomixer } from '$features/monitoring/shared/thermomixer.utils';

interface Props {
  snapshot: GetDeviceSnapshotDTO;
  telemetry: TelemetryHistoryEnrichedDTO;
}

export function MonitoringParameterWrapperSnapshot({ snapshot, telemetry }: Props) {
  const parameterAccessor = useDeviceParameterAccessor(snapshot.inventoryDevice);
  const config = useDeviceTypeConfig(snapshot.inventoryDevice);

  const isSensor = config?.type === DeviceTypes.MONITOR;
  const isCentrifugeLargeDevice = isCentrifugeLarge(snapshot.inventoryDevice);
  const isThermomixerDevice = isThermomixer(snapshot.inventoryDevice);
  const staticSetpointDisplay = checkStaticSetpointDisplay(
    snapshot.inventoryDevice,
    telemetry.telemetryId,
  );

  const isCountdownTimer = isCountdownTimerParameter(
    snapshot.inventoryDevice,
    telemetry.telemetryId,
  );
  const findShadowComp = isCountdownTimer
    ? findCountdownTimerShadowComp
    : findShadowCompByTelemetryId;

  const online = snapshot.currentShadow.activityState !== UnitStateV2.Offline.toString();
  return online ? (
    <MonitoringParameterSnapshot
      key={telemetry.telemetryId}
      name={telemetry.name}
      shadowComponent={findShadowComp(
        snapshot.currentShadow.components,
        telemetry.telemetryId,
      )}
      processValue={findTelemById(snapshot.latestTelemetry, telemetry.telemetryId)}
      fractionalDigits={telemetry.fractionalDigits}
      isSensor={isSensor}
      deviceId={snapshot.inventoryDevice.serialNumber}
      parameterConfig={parameterAccessor(telemetry)}
      isCentrifugeLarge={isCentrifugeLargeDevice}
      isThermomixer={isThermomixerDevice}
      hasStaticSetpointDisplay={staticSetpointDisplay}
      isCountdownTimer={isCountdownTimer}
      activityState={snapshot.currentShadow.activityState}
    />
  ) : (
    <MonitoringParameterOffline
      key={telemetry.telemetryId}
      name={telemetry.name}
      parameterConfig={parameterAccessor(telemetry)}
    />
  );
}
